/**
 * SectionGuestRequirements — qué se le pide al huésped antes de que llegue.
 *
 * La sección responde a una sola pregunta: qué datos y qué consentimientos
 * necesita la propiedad, y cuáles no. Por eso la lista de datos obligatorios
 * es el bloque más visible: cada casilla marcada es una pregunta más en el
 * check-in online, así que se lee como lo que es —una decisión sobre el trato
 * al huésped— y no como una configuración técnica.
 *
 * Dos decisiones que conviene explicar:
 *  - Inmigración. El producto no se conecta con ningún sistema público. Aquí
 *    solo se declara si la propiedad tiene ese deber; lo que el producto
 *    aporta es la cola de pendientes y la exportación de los datos. El envío
 *    automático (APOA) aparece marcado como futuro para que nadie lo confunda
 *    con una integración que ya existe.
 *  - Conservación de documentos. El valor de partida es «Por definir» a
 *    propósito: es una decisión aplazada, y esta vista previa no debe
 *    presentar ningún plazo como acordado ni como exigido por ley.
 *
 * Todo el estado vive en `settingsV2Store`; la sección no llama a la API.
 */
import React from "react";
import { ShieldCheck } from "@phosphor-icons/react";
import {
  Block, Card, FieldGrid, Select, Toggle, CheckRow, Note, Advanced, UsedIn, FutureChip,
} from "@/pages/admin/pms/settings/SettingsPrimitives";
import { useSettingsV2, updateSection, toggleGuestField } from "@/lib/settingsV2Store";
import { GUEST_FIELD_LABELS } from "@/data/settingsV2Demo";

/** Plazos de ejemplo. Ninguno está decidido: la opción viva es «Por definir». */
const RETENTION_OPTIONS = [
  { value: "pending", label: "Por definir" },
  { value: "checkout_30", label: "Hasta el check-out + 30 días" },
  { value: "checkout_90", label: "Hasta el check-out + 90 días" },
  { value: "local_law", label: "Según la normativa local" },
];

/** Para cada dato, por qué se pediría. Ayuda a no marcar casillas por inercia. */
const FIELD_HINTS = {
  full_name: "Tal y como figura en el documento de identidad",
  email: "Por donde llega el enlace del check-in y la confirmación",
  phone: "Para los avisos de llegada y los mensajes de la estancia",
  nationality: "Necesaria si hay que reportar huéspedes extranjeros",
  passport: "El huésped tendrá que subir una foto del documento",
  date_of_birth: "Algunos destinos la piden en el registro de viajeros",
  arrival_time: "Permite preparar el alojamiento y ordenar la limpieza del día",
  flight_number: "Solo tiene sentido si se ofrece recogida en el aeropuerto",
};

const FIELD_KEYS = Object.keys(GUEST_FIELD_LABELS);

export default function SectionGuestRequirements() {
  const { guests } = useSettingsV2();
  const set = (patch) => updateSection("guests", patch);

  const asked = FIELD_KEYS.filter((k) => guests.fields[k]).length;

  return (
    <div data-testid="section-guests">
      <Block
        title="Check-in online"
        description="El huésped completa sus datos antes de llegar, desde el móvil, sin papeles en la puerta."
        testId="guests-checkin"
      >
        <Card testId="guests-checkin-card">
          <div className="divide-y divide-stone-100 dark:divide-stone-800">
            <Toggle
              label="Ofrecer check-in online"
              description="Se envía un enlace privado antes de la llegada con los datos y los documentos que haga falta."
              checked={guests.online_checkin_enabled}
              onChange={(v) => set({ online_checkin_enabled: v })}
              usedIn="guest_requirements"
              testId="guests-checkin-enabled"
            />
            <Toggle
              label="Pedirlo antes de la llegada"
              description="La llegada no se da por preparada hasta que el check-in está completo. Si se desactiva, el enlace sigue disponible pero es opcional."
              checked={guests.online_checkin_required}
              onChange={(v) => set({ online_checkin_required: v })}
              disabled={!guests.online_checkin_enabled}
              testId="guests-checkin-required"
            />
          </div>

          {!guests.online_checkin_enabled && (
            <p
              className="mt-3 pt-3 border-t border-stone-100 dark:border-stone-800 text-xs text-stone-500 dark:text-stone-400"
              data-testid="guests-checkin-offline-hint"
            >
              Sin check-in online, los datos de abajo se piden a la llegada, en persona.
            </p>
          )}
        </Card>
      </Block>

      <Block
        title="Información obligatoria"
        description={
          <>
            Lo que el huésped tendrá que rellenar para completar su check-in.{" "}
            <UsedIn topic="guest_requirements" />
          </>
        }
        testId="guests-fields"
      >
        <Note testId="guests-fields-note">
          Solo se pedirá lo que esté marcado aquí. Todo lo demás no aparece en el formulario, así
          que al huésped nunca se le piden datos que la propiedad no necesita. Ahora mismo se piden{" "}
          {asked} de {FIELD_KEYS.length}.
        </Note>

        <Card testId="guests-fields-card">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
            {FIELD_KEYS.map((key) => (
              <CheckRow
                key={key}
                label={GUEST_FIELD_LABELS[key]}
                description={FIELD_HINTS[key]}
                checked={!!guests.fields[key]}
                onChange={() => toggleGuestField(key)}
                testId={`guests-field-${key.replace(/_/g, "-")}`}
              />
            ))}
          </div>
        </Card>
      </Block>

      <Block
        title="Inmigración"
        description="En Indonesia, informar de los huéspedes extranjeros es un deber de la propiedad, no del producto."
        testId="guests-immigration"
      >
        <Card testId="guests-immigration-card">
          <Toggle
            label="Esta propiedad debe reportar huéspedes extranjeros"
            description="Con esta opción activa, el producto reúne los datos, avisa de quién queda pendiente y prepara la exportación. El envío del informe lo hace siempre la propiedad."
            checked={guests.immigration_reporting_required}
            onChange={(v) => set({ immigration_reporting_required: v })}
            testId="guests-immigration-required"
          />

          <div
            className="mt-4 pt-4 border-t border-stone-100 dark:border-stone-800"
            data-testid="guests-immigration-apoa"
          >
            <div className="flex items-start gap-3">
              <ShieldCheck size={18} className="mt-0.5 shrink-0 text-stone-400" />
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-stone-500 dark:text-stone-400">
                    Envío automático al portal de inmigración (APOA)
                  </span>
                  <FutureChip>No existe todavía</FutureChip>
                </div>
                <p className="text-xs text-stone-500 dark:text-stone-400 mt-1 max-w-2xl">
                  No hay ninguna conexión con APOA ni con ningún otro sistema público, ni aquí ni
                  en el resto del producto. Esta fila solo deja constancia de una idea para más
                  adelante: hoy no se envía nada de forma automática y no hay nada que activar.
                </p>
              </div>
            </div>
          </div>
        </Card>
      </Block>

      <Block
        title="Consentimientos"
        description="Lo que el huésped confirma antes de terminar el check-in."
        testId="guests-consents"
      >
        <Card testId="guests-consents-card">
          <div className="divide-y divide-stone-100 dark:divide-stone-800">
            <Toggle
              label="Aceptar las normas de la casa"
              description="El huésped confirma que las ha leído. Las normas son las de la propiedad, así que se escriben una vez y se reutilizan."
              checked={guests.consent_house_rules}
              onChange={(v) => set({ consent_house_rules: v })}
              testId="guests-consent-house-rules"
            />
            <Toggle
              label="Aceptar el tratamiento de sus datos"
              description="Explica para qué se usan sus datos y sus documentos, y quién puede verlos."
              checked={guests.consent_privacy}
              onChange={(v) => set({ consent_privacy: v })}
              testId="guests-consent-privacy"
            />
          </div>

          <Advanced label="Dónde ve esto el huésped" testId="guests-consents-advanced">
            <p className="text-xs text-stone-500 dark:text-stone-400 max-w-2xl">
              Los consentimientos aparecen en el último paso del check-in, con el texto en el
              idioma del huésped, y quedan anotados en su reserva con la fecha en que los aceptó.
              En la vista previa no se guarda ningún texto ni ninguna aceptación.
            </p>
          </Advanced>
        </Card>
      </Block>

      <Block
        title="Conservación de documentos"
        description="Cuánto tiempo se guardan las fotos del pasaporte o del documento de identidad."
        testId="guests-retention"
      >
        <Note testId="guests-retention-note">
          El plazo está aplazado a propósito: todavía no se ha decidido y depende de lo que exija
          el destino. Lo que se elija aquí es solo un ejemplo de la vista previa, no un acuerdo ni
          una obligación legal.
        </Note>

        <Card testId="guests-retention-card">
          <FieldGrid>
            <Select
              label="Plazo de conservación"
              value={guests.document_retention}
              onChange={(v) => set({ document_retention: v })}
              options={RETENTION_OPTIONS}
              hint="Decisión pendiente"
              testId="guests-retention-select"
            />
          </FieldGrid>

          <p
            className="mt-4 pt-4 border-t border-stone-100 dark:border-stone-800 text-xs text-stone-500 dark:text-stone-400 max-w-2xl"
            data-testid="guests-retention-explainer"
          >
            Cuando haya un plazo, los documentos se borrarán solos al cumplirse, sin que nadie
            tenga que acordarse. Mientras tanto se guardan aparte de la ficha del huésped y solo
            los ve el equipo que los necesita.
          </p>
        </Card>
      </Block>
    </div>
  );
}
